import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, Star } from 'lucide-react';
import { Reward as RewardType, calculateRewardValue } from '@/utils/storage';

interface SpendMoneyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (amount: number) => void;
  reward: RewardType | null;
  totalStarsEarned: number;
  availableStars: number;
}

const SpendMoneyDialog: React.FC<SpendMoneyDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  reward,
  totalStarsEarned,
  availableStars
}) => {
  const [spendAmount, setSpendAmount] = useState('');
  const [error, setError] = useState('');

  if (!reward || !reward.currency) return null;

  const totalAvailable = calculateRewardValue(reward, totalStarsEarned);
  const requiredStars = Math.ceil((parseFloat(spendAmount) || 0) / (reward.rate || 1)) * (reward.threshold || 1);

  const handleAmountChange = (value: string) => {
    setSpendAmount(value);
    setError('');
    
    const amount = parseFloat(value);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (amount > totalAvailable) {
      setError(`Maximum available: ${reward.currency}${totalAvailable}`);
      return;
    }
    
    const starsNeeded = Math.ceil(amount / (reward.rate || 1)) * (reward.threshold || 1);
    if (starsNeeded > availableStars) {
      setError(`Not enough stars! Need ${starsNeeded} stars but only have ${availableStars}`);
      return;
    }
  };

  const handleConfirm = () => {
    const amount = parseFloat(spendAmount);
    if (!error && amount > 0) {
      onConfirm(amount);
      setSpendAmount('');
      setError('');
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSpendAmount('');
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="text-green-500" size={20} />
            Spend Money
          </DialogTitle>
          <DialogDescription>
            How much would you like to spend from "{reward.title}"?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium">Total Available:</span>
            <span className="text-lg font-bold text-green-600">
              {reward.currency}{totalAvailable}
            </span>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="spend-amount">Amount to spend:</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                {reward.currency}
              </span>
              <Input
                id="spend-amount"
                type="number"
                value={spendAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0.00"
                className="pl-7"
                min="0"
                max={totalAvailable}
                step="0.01"
              />
            </div>
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
          </div>
          
          {spendAmount && !error && (
            <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <span className="text-sm">Stars required:</span>
              <span className="flex items-center gap-1 font-medium">
                <Star size={16} className="text-yellow-400 fill-yellow-400" />
                {requiredStars}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!spendAmount || !!error || parseFloat(spendAmount) <= 0}
          >
            Spend {reward.currency}{spendAmount || '0'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SpendMoneyDialog;